import React from "react";
import ReactDOM from "react-dom/client";

/**
 * @typedef {object} RpaPreferredTeammatesProps
 * @property {string} rpaReservationIds
 * @property {string} rpaResourceId
 * @property {string} rpaScheduleGridId
 * @property {string} date
 * @property {string} csrf
 * @property {string} returnUrl
 */

/**
 * @typedef {object} RpaPreferredTeammatesItem
 * @property {string} name
 * @property {number} value
 * @property {number} key
 */

/**
 * @typedef {object} RpaPreferredTeammatesStateObj
 * @property {string} rpaReservationIds
 * @property {string} rpaResourceId
 * @property {string} rpaScheduleGridId
 * @property {string} date
 * @property {string} csrf
 * @property {string} returnUrl
 * @property {Array<RpaPreferredTeammatesItem>} preferredTeammates
 * @property {Array<RpaPreferredTeammatesItem>} filteredPreferredTeammates
 * @property {boolean} connectedFriends
 * @property {boolean} isOpen - Indique si le composant est ouvert ou fermé
 */

class RpaPreferredTeammates extends React.Component {
  /**
   * @param {RpaPreferredTeammatesProps} props - The component props
   */
  constructor(props) {
    super(props);

    this.state = /** @type {RpaPreferredTeammatesStateObj} */ ({
      rpaReservationIds: props.rpaReservationIds,
      rpaResourceId: props.rpaResourceId,
      rpaScheduleGridId: props.rpaScheduleGridId,
      date: props.date,
      csrf: props.csrf,
      returnUrl: props.returnUrl,
      preferredTeammates: [],
      filteredPreferredTeammates: [],
      connectedFriends: false,
      isOpen: true
    });
  }

  async componentDidMount() {
    await this.refreshList();
  }

  /**
   * Permet de rafraîchir la liste des coéquipiers préférés en la récupérant à nouveau via l'API
   */
  async refreshList() {
    var result = await this._fetchPreferredTeammates();

    this.setState({
      preferredTeammates: result.preferredTeammates,
      filteredPreferredTeammates: result.preferredTeammates
    });
  }

  /**
   * Permet de récupérer la liste des coéquipiers préférés via l'API
   * @returns {Promise<{preferredTeammates: Array<RpaPreferredTeammatesItem>}>} La liste des coéquipiers préférés
   * @private
   */
  async _fetchPreferredTeammates() {
    var apiUrl = `/api/rpa-reservation/_preferredteammates?rpaReservationIds=${this.state.rpaReservationIds}&connectedFriends=${this.state.connectedFriends}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      });

    return await response.json();
  }

  /**
   * Permet de générer l'URL de suppression d'un coéquipier habituel
   * @param {Number} preferredTeammateId - Identifiant du coéquipier habituel
   * @returns {string} URL de suppression du coéquipier habituel
   */
  getDeleteTeammateHref(preferredTeammateId) {
    return `/rpa-reservation/_deleteteammate/${this.state.date}/${preferredTeammateId}?rpaResourceId=${this.state.rpaResourceId}&rpaScheduleGridId=${this.state.rpaScheduleGridId}`;
  }

  /**
   * Permet d'ajouter un coéquipier habituel à la réservation
   * @param {RpaPreferredTeammatesItem} preferredTeammate - Le coéquipier habituel à ajouter à la réservation
   */
  onClick(preferredTeammate) {
    fetch("/api/rpa-reservation/_add", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "gestiaweb"
      },
      body: JSON.stringify({
        rpaResourceId: this.state.rpaResourceId,
        rpaScheduleGridId: this.state.rpaScheduleGridId,
        date: this.state.date,
        userId: preferredTeammate.value,
        _csrf: this.state.csrf
      })
    })
      .then(res => res.json())
      .then(result => {
        document.location.href = result.url;
      });
  }

  /**
   * Lors du changement du champ de recherche
   * @param {import("react").ChangeEvent<any>} e - L'événement de changement du champ de recherche
   * The search input uses an appropriate aria-label for screen readers.
   */
  onChangeOnSearch(e) {
    var search = /** @type {HTMLInputElement} */ (e.target).value.toLowerCase(),
      filteredPreferredTeammates;

    this.setState(
      {
        filteredPreferredTeammates: this.state.preferredTeammates
      },
      () => {
        filteredPreferredTeammates = this.state.filteredPreferredTeammates;

        filteredPreferredTeammates = filteredPreferredTeammates.filter(item => {
          var regExp = new RegExp(search, "g");
          return item.name.toLowerCase().match(regExp);
        });

        this.setState({
          filteredPreferredTeammates
        });
      }
    );
  }

  /**
   * Gère le changement d'état du checkbox
   * @param {import("react").ChangeEvent<any>} e
   */
  onClickOnCheckbox(e) {
    var checked = e.target.checked;

    this.setState({ connectedFriends: checked }, async () => {
      await this.refreshList();
    });
  }

  /**
   * Lors du clic sur l'en-tête du composant, on inverse l'état d'ouverture du composant
   */
  _onClickOnHeader() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  render() {
    return (
      <div className={this.state.isOpen ? "rpapreferedteammates" : "rpapreferedteammates rpapreferedteammates--closed"}>
        <div className="rpapreferedteammates__header" onClick={this._onClickOnHeader.bind(this)}>
          Coéquipiers préférés
          <div className="rpapreferedteammates__header-icon">{this.state.isOpen ? "keyboard_arrow_down" : "keyboard_arrow_up"}</div>
        </div>
        <div className="rpapreferedteammates__content">
          <div style={{ display: "grid", gridTemplateRows: "auto 1fr", overflowY: "auto", height: "100%", gap: "8px" }}>
            <div>
              <div className="flexline flexline--margin flexline--align-vz flexline--no-mobile">
                <div className="switch-container">
                  <input type="checkbox" id="connectedFriendsCheckbox" checked={this.state.connectedFriends} onChange={this.onClickOnCheckbox.bind(this)} aria-label="Afficher uniquement les coéquipiers connectés entre eux" />
                  <label htmlFor="connectedFriendsCheckbox">Connectés entre eux</label>
                </div>
                <div className="input-group input-group--no-margin" style={{ flexGrow: 1 }}>
                  <input id="searchPreferredTeammate" className="input-group__input" onChange={this.onChangeOnSearch.bind(this)} placeholder="Recherchez parmi la liste" aria-label="Recherchez parmi la liste" />
                </div>
              </div>
            </div>
            <div style={{ overflowY: "auto" }}>
              <div className="grid grid--has-5-child no-mobile grid--has-2-child-mobile grid--8-gap">
                {this.state.filteredPreferredTeammates.map((preferredTeammate, index) => (
                  <div className="buttongroup" key={index}>
                    <button className="buttongroup__button" style={{ flexGrow: 1 }} data-icon="add" onClick={this.onClick.bind(this, preferredTeammate)}>
                      {preferredTeammate.name}
                    </button>
                    <a className="buttongroup__button" href={this.getDeleteTeammateHref(preferredTeammate.value)}>
                      <span className="material-icons">delete</span>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

var rpaPreferredTeammatesNode = document.getElementById("rpa-preferred-teammates");
if (rpaPreferredTeammatesNode) {
  var root = ReactDOM.createRoot(rpaPreferredTeammatesNode);
  root.render(<RpaPreferredTeammates rpaReservationIds={rpaPreferredTeammatesNode.getAttribute("data-rpareservationids")} rpaResourceId={rpaPreferredTeammatesNode.getAttribute("data-rparesourceid")} rpaScheduleGridId={rpaPreferredTeammatesNode.getAttribute("data-rpaschedulegridid")} date={rpaPreferredTeammatesNode.getAttribute("data-date")} csrf={rpaPreferredTeammatesNode.getAttribute("data-csrf")} returnUrl={rpaPreferredTeammatesNode.getAttribute("data-returnurl")} />);
}
